import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import WappIcono from "../../../components/wappIcono";
import { Sticker } from "../../../services/v2/prospects.service";
import { CrearSticker, IconoSticker, PanelStickers } from "./stickers";
import "./equipoGifs.css";
import "./stickers.css";

/**
 * Selector de emojis del compositor de WappTeam (Esteban, 2026-10-11: "no se pueden mandar emojis desde el texto").
 * Como el de WhatsApp: categorías + Recientes (en este navegador). Se queda abierto para poner varios; el emoji entra donde está el cursor.
 */
export const GRUPOS: { n: string; i: string; e: string }[] = [
	{ n: "Caras", i: "😀", e: "😀 😃 😄 😁 😆 😅 🤣 😂 🙂 🙃 😉 😊 😇 🥰 😍 🤩 😘 😗 😚 😋 😛 😜 🤪 😝 🤑 🤗 🤭 🤫 🤔 🤐 🤨 😐 😑 😶 😏 😒 🙄 😬 😮‍💨 🤥 😌 😔 😪 🤤 😴 😷 🤒 🤕 🤢 🤮 🥵 🥶 🥴 😵 🤯 🤠 🥳 😎 🤓 🧐 😕 😟 🙁 😮 😯 😲 😳 🥺 😦 😧 😨 😰 😥 😢 😭 😱 😖 😣 😞 😓 😩 😫 🥱 😤 😡 😠 🤬 😈 💀 💩 🤡 👻 👽 🤖 😺 😸 😹 😻 🙈 🙉 🙊" },
	{ n: "Manos", i: "👍", e: "👍 👎 👌 🤌 ✌️ 🤞 🤟 🤘 🤙 👈 👉 👆 👇 ☝️ ✋ 🤚 🖐️ 🖖 👋 👏 🙌 👐 🤲 🤝 🙏 ✍️ 💪 🫶 👀 🧠 🗣️ 👤 👥 🧑‍🏫 👩‍🏫 👨‍🏫 🧑‍🎓 👩‍🎓 👨‍🎓 🧑‍💼 👩‍💼 👨‍💼 🧑‍🍳 💁 🙋 🤷 🤦 🙆 🙅 🏃 🚶" },
	{ n: "Corazones", i: "❤️", e: "❤️ 🧡 💛 💚 💙 💜 🖤 🤍 🤎 💔 ❣️ 💕 💞 💓 💗 💖 💘 💝 💯 💢 💥 💫 💦 💨 🔥 ✨ ⭐ 🌟 ⚡ 💤" },
	{ n: "Fiesta", i: "🎉", e: "🎉 🎊 🥳 🎈 🎁 🎂 🍰 🧁 🍾 🥂 🍻 🏆 🥇 🥈 🥉 🏅 🎖️ 🎯 🎓 🎶 🎵 🎤 📸 🌹 💐 🌻 🌈 ☀️ 🌙 ⛄ 🎄 🎃" },
	{ n: "Trabajo", i: "💼", e: "💼 📚 📖 📝 ✏️ 📌 📍 📎 📋 📅 📆 🗓️ ⏰ ⏳ ⌛ 📞 ☎️ 📱 💻 🖥️ 🖨️ 📧 📨 📩 📤 📥 📢 📣 🔔 🔕 💬 💭 🗂️ 📁 📊 📈 📉 💰 💵 💳 🧾 🏦 🏫 🏢 🚗 🚌 ✈️ 🛳️ ⚓ 🍽️ ☕ 🍕 🌮 🔑 🔒 🔓 🛠️ ⚙️ 🧹 🧼" },
	{ n: "Símbolos", i: "✅", e: "✅ ☑️ ✔️ ❌ ❎ ⚠️ 🚫 ⛔ ❗ ❓ ‼️ ⁉️ ➕ ➖ ➡️ ⬅️ ⬆️ ⬇️ 🔄 🔁 🆗 🆕 🆓 🔝 🔴 🟠 🟡 🟢 🔵 🟣 ⚫ ⚪ 🟥 🟧 🟨 🟩 🟦 🟪 ⬛ ⬜ 1️⃣ 2️⃣ 3️⃣ 4️⃣ 5️⃣ 🇲🇽 🇭🇳 🇸🇻 🇺🇸" },
];
const CLAVE = "wappteam_emojis_recientes";
const leerRecientes = (): string[] => { try { return JSON.parse(localStorage.getItem(CLAVE) || "[]").slice(0, 32); } catch (e) { return []; } };

/** Teléfono o tableta: el teclado en pantalla tapa lo que esté pegado abajo y el foco con letra chica hace zoom en iPhone. */
export const esTactil = (): boolean => { try { return window.matchMedia("(max-width: 700px), (pointer: coarse)").matches; } catch (e) { return false; } };

/**
 * Enter en los cuadros de escribir (Esteban, 2026-10-12): Enter manda; Ctrl+Enter (o Cmd/Alt) y Shift+Enter ponen un salto
 * de línea. En teléfono/tableta (sin Shift ni Ctrl a la mano) Enter sigue siendo salto de línea, como WhatsApp; se manda con
 * el botón. Devuelve true si atendió la tecla.
 */
export const teclaEnter = (e: React.KeyboardEvent<HTMLTextAreaElement>, enviar: () => void): boolean => {
	if (e.key !== "Enter" || (e.nativeEvent as any).isComposing) return false;
	if (e.shiftKey) return true;   // salto de línea normal del navegador
	const el = e.currentTarget;
	if (e.ctrlKey || e.metaKey || e.altKey) {
		e.preventDefault();
		// execCommand deja deshacer (Ctrl+Z) y dispara onChange; si no existe, setRangeText + evento input.
		let ok = false;
		try { ok = document.execCommand("insertText", false, "\n"); } catch (err) { ok = false; }
		if (!ok) { el.setRangeText("\n", el.selectionStart, el.selectionEnd, "end"); el.dispatchEvent(new Event("input", { bubbles: true })); }
		return true;
	}
	let tactil = false;
	try { tactil = window.matchMedia("(pointer: coarse)").matches; } catch (err) { tactil = false; }
	if (tactil) return true;
	e.preventDefault();
	enviar();
	return true;
};

// ------------------------------------------------------------------ todos los emojis (Esteban, 2026-10-14)

/**
 * "No todos los emojis están disponibles": la lista de arriba (GRUPOS) era una selección de ~400. Ahora el panel trae los
 * 1,898 de Unicode 15.1 (los que ya pintan iPhone, Android y Windows) con nombres en español para buscar y tonos de piel.
 * Sale de emojibase-data (es) con scripts/emojis-datos.py; ~33 KB comprimido, en su propio pedazo: el navegador lo baja la
 * primera vez que alguien abre el panel y lo guarda. Cero consultas al servidor de WAPP.
 * Cada emoji: [emoji, palabras para buscar (sin acentos), los 5 tonos de piel si tiene].
 */
type EmojiDato = [string, string, string[]?];
type GrupoDato = { n: string; i: string; e: EmojiDato[] };
let datos: Promise<GrupoDato[]> | null = null;
const cargarEmojis = (): Promise<GrupoDato[]> => {
	if (!datos) {
		datos = import("./emojisDatos.json").then((m: any) => (m.default || m) as GrupoDato[]);
		datos.catch(() => { datos = null; });
	}
	return datos;
};
const sinAcentos = (t: string) => t.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
const CLAVE_TONO = "wappteam_emoji_tono";
const leerTono = (): number => { try { const n = +(localStorage.getItem(CLAVE_TONO) || 0); return n >= 0 && n <= 5 ? n : 0; } catch (e) { return 0; } };
const MANO = "✋";
const MANOS_TONO = ["✋", "✋🏻", "✋🏼", "✋🏽", "✋🏾", "✋🏿"];
const conTono = (d: EmojiDato, tono: number) => (tono && d[2] ? d[2][tono - 1] : d[0]);

const guardarReciente = (e: string, recientes: string[]): string[] => {
	const r = [e, ...recientes.filter((x) => x !== e)].slice(0, 32);
	try { localStorage.setItem(CLAVE, JSON.stringify(r)); } catch (err) { /* sin almacenamiento: solo no recuerda */ }
	return r;
};

/** Lo de adentro del panel de emojis: buscador, tonos, categorías y la rejilla. Lo usan el compositor y las reacciones. */
export const PanelEmojis: React.FC<{ onElegir: (emoji: string) => void; autoFocoBuscar?: boolean }> = ({ onElegir, autoFocoBuscar }) => {
	const [grupos, setGrupos] = useState<GrupoDato[] | null>(null);
	const [fallo, setFallo] = useState(false);
	const [recientes, setRecientes] = useState<string[]>(leerRecientes);
	// La pestaña se fija al abrir: antes saltaba de Caras a Recientes tras el primer emoji y el segundo toque caía en otro.
	const [grupo, setGrupo] = useState(() => (leerRecientes().length ? -1 : 0));
	const [q, setQ] = useState("");
	const [tono, setTono] = useState(leerTono);
	const [eligeTono, setEligeTono] = useState(false);
	const rejilla = useRef<HTMLDivElement>(null);
	const buscar = useRef<HTMLInputElement>(null);
	useEffect(() => {
		let vivo = true;
		cargarEmojis().then((g) => { if (vivo) setGrupos(g); }).catch(() => { if (vivo) setFallo(true); });
		if (autoFocoBuscar && !esTactil()) buscar.current?.focus({ preventScroll: true });
		return () => { vivo = false; };
	}, [autoFocoBuscar]);
	useEffect(() => { rejilla.current?.scrollTo({ top: 0 }); }, [grupo, q]);
	const elegir = (e: string) => { setRecientes(guardarReciente(e, recientes)); onElegir(e); };
	const ponerTono = (t: number) => { setTono(t); setEligeTono(false); try { localStorage.setItem(CLAVE_TONO, String(t)); } catch (err) { /* nada */ } };
	const busqueda = sinAcentos(q.trim());
	let lista: string[] = [];
	let titulo = "";
	if (busqueda && grupos) {
		const palabras = busqueda.split(/\s+/);
		for (const g of grupos) for (const d of g.e) {
			const t = " " + d[1];
			if (palabras.every((w) => t.indexOf(" " + w) >= 0)) lista.push(conTono(d, tono));
			if (lista.length >= 240) break;
		}
		titulo = lista.length ? "Resultados" : "Sin resultados para \u201c" + q.trim() + "\u201d";
	} else if (grupo === -1) {
		lista = recientes;
		titulo = "Recientes";
	} else if (grupos) {
		const g = grupos[Math.min(grupo, grupos.length - 1)];
		lista = g.e.map((d) => conTono(d, tono));
		titulo = g.n;
	} else {
		// Mientras baja el catálogo completo (o si no se pudo): la lista corta de siempre.
		const g = GRUPOS[Math.min(Math.max(grupo, 0), GRUPOS.length - 1)];
		lista = g.e.split(" ");
		titulo = g.n;
	}
	const pestanas = grupos ? grupos.map((g) => ({ n: g.n, i: g.i })) : GRUPOS.map((g) => ({ n: g.n, i: g.i }));
	return (
		<>
			<div className="eqe-cabeza">
				{/* div, no label: WAPP tiene una regla global que pone todo <label> en position:absolute. */}
				<div className="eqg-buscar eqe-buscar">
					<WappIcono n="search" />
					<input ref={buscar} type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar emoji" aria-label="Buscar emoji"
						onKeyDown={(e) => { if (e.key === "Enter" && lista.length) { e.preventDefault(); elegir(lista[0]); } }} />
				</div>
				<div className="eqe-tono">
					<button type="button" className={eligeTono ? "activo" : ""} onMouseDown={(e) => e.preventDefault()} onClick={() => setEligeTono(!eligeTono)}
						title="Tono de piel" aria-label="Tono de piel" aria-expanded={eligeTono}>{MANOS_TONO[tono] || MANO}</button>
					{eligeTono ? (
						<div className="eqe-tonos" role="listbox" aria-label="Tono de piel">
							{MANOS_TONO.map((m, i) => <button key={i} type="button" className={i === tono ? "activo" : ""} onMouseDown={(e) => e.preventDefault()} onClick={() => ponerTono(i)}
								aria-label={i ? "Tono " + i : "Sin tono"}>{m}</button>)}
						</div>
					) : null}
				</div>
			</div>
			{!busqueda ? (
				<div className="eqe-tabs">
					{recientes.length ? <button type="button" className={grupo === -1 ? "activo" : ""} onMouseDown={(e) => e.preventDefault()} onClick={() => setGrupo(-1)} title="Recientes" aria-label="Recientes">🕘</button> : null}
					{pestanas.map((x, i) => (
						<button key={x.n} type="button" className={grupo === i ? "activo" : ""} onMouseDown={(e) => e.preventDefault()} onClick={() => setGrupo(i)} title={x.n} aria-label={x.n}>{x.i}</button>
					))}
				</div>
			) : null}
			<div className="eqg-titulo">{titulo}{!grupos && !fallo && grupo !== -1 ? " · cargando todos…" : ""}</div>
			<div className="eqe-rejilla" ref={rejilla}>
				{lista.map((e) => <button key={e} type="button" onMouseDown={(ev) => ev.preventDefault()} onClick={() => elegir(e)} aria-label={e}>{e}</button>)}
			</div>
		</>
	);
};

/**
 * Panel de emojis suelto, al centro de la pantalla: el "+" de las reacciones (WappTeam y WappChat) para reaccionar con
 * cualquier emoji, como WhatsApp.
 */
export const EmojisFlotante: React.FC<{ onElegir: (emoji: string) => void; onCerrar: () => void }> = ({ onElegir, onCerrar }) => {
	useEffect(() => {
		const esc = (e: KeyboardEvent) => { if (e.key === "Escape") onCerrar(); };
		document.addEventListener("keydown", esc);
		return () => document.removeEventListener("keydown", esc);
	}, [onCerrar]);
	return createPortal(
		<div className="eqe-velo" onMouseDown={(e) => { if (e.target === e.currentTarget) onCerrar(); }} onClick={(e) => e.stopPropagation()}>
			<div className="eqg-panel eqe-panel eqe-flotante" role="dialog" aria-label="Reaccionar con un emoji">
				<PanelEmojis autoFocoBuscar onElegir={(e) => { onElegir(e); onCerrar(); }} />
			</div>
		</div>,
		document.body,
	);
};

/** Lo que el compositor necesita para la pestaña Stickers (si no viene, el panel es solo de emojis). */
export interface StickersDelPanel {
	campusId: number;
	onEnviar: (s: Sticker) => Promise<unknown> | void;
}

/** enBarra: el botón va dentro del cuadro de escribir, como WhatsApp (Esteban, 2026-10-12). */
export const BotonEmoji: React.FC<{ onElegir: (emoji: string) => void; compacto?: boolean; disabled?: boolean; enBarra?: boolean; stickers?: StickersDelPanel }> = ({ onElegir, compacto, disabled, enBarra, stickers }) => {
	const [abierto, setAbierto] = useState(false);
	const [vista, setVista] = useState<"emojis" | "stickers">("emojis");
	const [creando, setCreando] = useState(false);
	const caja = useRef<HTMLDivElement>(null);
	useEffect(() => {
		if (!abierto || creando) return;
		const fuera = (e: MouseEvent) => { if (caja.current && !caja.current.contains(e.target as Node)) setAbierto(false); };
		const esc = (e: KeyboardEvent) => { if (e.key === "Escape") setAbierto(false); };
		document.addEventListener("mousedown", fuera);
		document.addEventListener("keydown", esc);
		return () => { document.removeEventListener("mousedown", fuera); document.removeEventListener("keydown", esc); };
	}, [abierto, creando]);
	return (
		<div className={"eqg" + (compacto ? " compacto" : "") + (enBarra ? " en-barra" : "")} ref={caja}>
			<button type="button" className={"eqg-boton eqe-boton" + (abierto ? " activo" : "")} onMouseDown={(e) => e.preventDefault()}
				onClick={() => {
					// En el teléfono se baja el teclado para que el panel (pegado abajo) se vea, como WhatsApp.
					const act = document.activeElement;
					if (!abierto && esTactil() && act instanceof HTMLElement) {
						if (act instanceof HTMLTextAreaElement) act.dataset.cursor = String(act.selectionStart);
						act.blur();
					}
					setAbierto(!abierto);
				}}
				disabled={disabled} aria-label={stickers ? "Emojis y stickers" : "Emojis"} title={stickers ? "Emojis y stickers" : "Emojis"} aria-expanded={abierto}><WappIcono n="smile" /></button>
			{abierto ? (
				<div className={"eqg-panel eqe-panel" + (stickers ? " con-stickers" : "")} role="dialog" aria-label="Emojis">
					{vista === "emojis" || !stickers ? <PanelEmojis onElegir={onElegir} />
						: <PanelStickers campusId={stickers.campusId} onCrear={() => setCreando(true)}
							onElegir={async (s) => { await stickers.onEnviar(s); setAbierto(false); }} />}
					{stickers ? (
						<div className="eqe-pie" role="tablist">
							<button type="button" role="tab" aria-selected={vista === "emojis"} className={vista === "emojis" ? "activo" : ""} onMouseDown={(e) => e.preventDefault()} onClick={() => setVista("emojis")}>
								<WappIcono n="smile" /> Emojis</button>
							<button type="button" role="tab" aria-selected={vista === "stickers"} className={vista === "stickers" ? "activo" : ""} onMouseDown={(e) => e.preventDefault()} onClick={() => setVista("stickers")}>
								<IconoSticker /> Stickers</button>
						</div>
					) : null}
				</div>
			) : null}
			{creando && stickers ? <CrearSticker campusId={stickers.campusId} onCerrar={() => setCreando(false)} onCreado={() => { setCreando(false); setVista("stickers"); }} /> : null}
		</div>
	);
};

/** Mete el texto donde está el cursor del textarea y deja el cursor después. */
export const insertarEnCursor = (el: HTMLTextAreaElement | null, actual: string, nuevo: string, set: (v: string) => void) => {
	// Sin foco (teléfono, teclado abajo) se usa la posición que se guardó en la inserción anterior.
	const guardado = el && esTactil() && document.activeElement !== el && el.dataset.cursor ? Math.min(+el.dataset.cursor, actual.length) : null;
	const ini = guardado ?? (el ? el.selectionStart : actual.length);
	const fin = guardado ?? (el ? el.selectionEnd : actual.length);
	set(actual.slice(0, ini) + nuevo + actual.slice(fin));
	if (!el) return;
	el.dataset.cursor = String(ini + nuevo.length);
	el.addEventListener("focus", () => { delete el.dataset.cursor; }, { once: true });
	// En el teléfono no se regresa el foco: abriría el teclado encima del panel con cada emoji.
	if (esTactil() && document.activeElement !== el) return;
	requestAnimationFrame(() => { el.focus({ preventScroll: true }); el.setSelectionRange(ini + nuevo.length, ini + nuevo.length); });
};
